/**
 * Fields: element properties exposed to PRISM and other controllers (text content, image source, colour,
 * visibility, opacity). Pure commands plus helpers for the Data panel.
 */
import type { BindingProperty, Element, Field, Scene } from '@prism-graphics/scene-schema';
import { fieldIdFrom, FIELD_ID } from './ids.ts';
import { findElement, updateElement } from './tree.ts';

export type Exposable = 'text' | 'color' | 'strokeColor' | 'visible' | 'image' | 'opacity';

/** The properties of an element that can be exposed as a field. */
export function exposableProperties(el: Element): Exposable[] {
  const out: Exposable[] = [];
  if (el.type === 'text') out.push('text', 'color');
  if (el.type === 'image') out.push('image');
  if ((el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') && (el.style.fill?.type ?? 'solid') === 'solid') out.push('color');
  if ((el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') && el.style.stroke) out.push('strokeColor');
  if (el.type === 'text' && el.style.stroke) out.push('strokeColor');
  out.push('visible', 'opacity');
  return out;
}

export function fieldFor(scene: Scene, elementId: string, property: BindingProperty): Field | undefined {
  return scene.fields.find((f) => f.bindings.some((b) => b.element === elementId && b.property === property));
}

/** The element's current value of a property, as the new field's default. */
function currentValue(scene: Scene, el: Element, property: Exposable): Field['default'] {
  switch (property) {
    case 'text':
      return el.type === 'text' ? el.text : '';
    case 'color':
      if (el.type === 'text') return el.style.color;
      if (el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') {
        const f = el.style.fill;
        if (f?.type === 'solid') return f.color;
      }
      return '#FFFFFF';
    case 'strokeColor':
      if (el.type === 'text') return el.style.stroke?.color ?? '#000000';
      if (el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') return el.style.stroke?.color ?? '#000000';
      return '#000000';
    case 'visible':
      return el.visible;
    case 'opacity':
      return el.transform.opacity;
    case 'image':
      return el.type === 'image' ? el.asset : scene.assets[0]?.hash ?? '';
  }
}

const TITLE_SUFFIX: Record<Exposable, string> = { text: '', color: ' colour', strokeColor: ' stroke colour', visible: ' visible', image: ' image', opacity: ' opacity' };

/** Expose a property as a new field (bound to the element). Returns the field id (existing one if already bound). */
export function exposeField(scene: Scene, elementId: string, property: Exposable): { scene: Scene; fieldId: string } {
  const existing = fieldFor(scene, elementId, property);
  if (existing) return { scene, fieldId: existing.id };
  const el = findElement(scene, elementId);
  if (!el) throw new Error(`unknown element ${elementId}`);
  const title = `${el.name || el.id}${TITLE_SUFFIX[property]}`.trim();
  const id = fieldIdFrom(title, new Set(scene.fields.map((f) => f.id)));
  const order = Math.max(0, ...scene.fields.map((f) => f.order ?? 0)) + 1;
  const bindings = [{ element: elementId, property }];
  const value = currentValue(scene, el, property);
  let field: Field;
  switch (property) {
    case 'text':
      field = { id, title, type: 'string', default: String(value), hidden: false, order, bindings, multiline: false, maxLength: Math.max(60, String(value).length) };
      break;
    case 'color':
    case 'strokeColor':
      field = { id, title, type: 'color', default: String(value), hidden: true, order, bindings };
      break;
    case 'visible':
      field = { id, title, type: 'boolean', default: Boolean(value), hidden: true, order, bindings };
      break;
    case 'opacity':
      field = { id, title, type: 'number', default: Number(value), minimum: 0, maximum: 1, integer: false, hidden: true, order, bindings };
      break;
    case 'image':
      field = { id, title, type: 'image-choice', default: String(value), choices: [String(value)], hidden: true, order, bindings };
      break;
  }
  return { scene: { ...scene, fields: [...scene.fields, field] }, fieldId: id };
}

/** Remove the binding of a property; a field left without bindings is removed. */
export function unexposeField(scene: Scene, elementId: string, property: BindingProperty): Scene {
  const hit = (b: Field['bindings'][number]) => b.element === elementId && b.property === property;
  const fields = scene.fields.flatMap((f) => {
    if (!f.bindings.some(hit)) return [f];
    const bindings = f.bindings.filter((b) => !hit(b));
    return bindings.length > 0 ? [{ ...f, bindings } as Field] : [];
  });
  return dropUpdateTransitions({ ...scene, fields });
}

export function removeField(scene: Scene, fieldId: string): Scene {
  return dropUpdateTransitions({ ...scene, fields: scene.fields.filter((f) => f.id !== fieldId) });
}

function dropUpdateTransitions(scene: Scene): Scene {
  const ids = new Set(scene.fields.map((f) => f.id));
  const tf = scene.timeline.update.fields;
  if (Object.keys(tf).every((k) => ids.has(k))) return scene;
  return { ...scene, timeline: { ...scene.timeline, update: { ...scene.timeline.update, fields: Object.fromEntries(Object.entries(tf).filter(([k]) => ids.has(k))) } } };
}

/**
 * Change a field. Renaming its id carries the update transition along. A new default for a text field is also
 * written to the bound text elements, so the element shows what the graphic shows without data.
 */
export function updateField(scene: Scene, fieldId: string, patch: Partial<Field>): Scene {
  const old = scene.fields.find((f) => f.id === fieldId);
  if (!old) return scene;
  const next = { ...old, ...patch } as Field;
  for (const k of Object.keys(next) as Array<keyof Field>) if (next[k] === undefined) delete (next as Record<string, unknown>)[k];
  if (next.id !== fieldId && (!FIELD_ID.test(next.id) || scene.fields.some((f) => f.id === next.id))) next.id = fieldId;
  let s: Scene = { ...scene, fields: scene.fields.map((f) => (f.id === fieldId ? next : f)) };
  if (next.id !== fieldId && fieldId in s.timeline.update.fields) {
    const entries = Object.entries(s.timeline.update.fields).map(([k, v]) => [k === fieldId ? next.id : k, v] as const);
    s = { ...s, timeline: { ...s.timeline, update: { ...s.timeline.update, fields: Object.fromEntries(entries) } } };
  }
  if ('default' in patch) s = applyDefaultToElements(s, next);
  return s;
}

/** Write a field's default into its bound elements' own properties (text, colour, visibility, opacity, image). */
export function applyDefaultToElements(scene: Scene, field: Field): Scene {
  let s = scene;
  for (const b of field.bindings) {
    s = updateElement(s, b.element, (el) => {
      const v = field.default;
      if (b.property === 'text' && el.type === 'text' && typeof v === 'string') return { ...el, text: v };
      if (b.property === 'color' && typeof v === 'string') {
        if (el.type === 'text') return { ...el, style: { ...el.style, color: v } };
        if (el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') return { ...el, style: { ...el.style, fill: { type: 'solid', color: v } } } as Element;
      }
      if (b.property === 'visible' && typeof v === 'boolean') return { ...el, visible: v };
      if (b.property === 'opacity' && typeof v === 'number') return { ...el, transform: { ...el.transform, opacity: v } };
      if (b.property === 'image' && el.type === 'image' && typeof v === 'string') return { ...el, asset: v };
      return el;
    });
  }
  return s;
}

/** Fields in UI order (order, then position). */
export function sortedFields(scene: Scene): Field[] {
  return scene.fields.map((f, i) => ({ f, i })).sort((a, b) => (a.f.order ?? a.i) - (b.f.order ?? b.i) || a.i - b.i).map((x) => x.f);
}

/** Move a field up or down in the UI order (renumbers order 1..n). */
export function moveField(scene: Scene, fieldId: string, dir: -1 | 1): Scene {
  const list = sortedFields(scene);
  const i = list.findIndex((f) => f.id === fieldId);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= list.length) return scene;
  [list[i], list[j]] = [list[j]!, list[i]!];
  const order = new Map(list.map((f, n) => [f.id, n + 1]));
  return { ...scene, fields: scene.fields.map((f) => ({ ...f, order: order.get(f.id) }) as Field) };
}

/** Problems with a test value against its field's validation (shown in the Data panel). */
export function validateValue(field: Field, value: unknown): string | undefined {
  switch (field.type) {
    case 'string':
      if (typeof value !== 'string') return 'not text';
      if (field.maxLength !== undefined && [...value].length > field.maxLength) return `longer than ${field.maxLength} characters (the graphic cuts it)`;
      if (field.enum && !field.enum.includes(value)) return 'not one of the allowed values';
      return undefined;
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) return 'not a number';
      if (field.minimum !== undefined && value < field.minimum) return `below ${field.minimum}`;
      if (field.maximum !== undefined && value > field.maximum) return `above ${field.maximum}`;
      return undefined;
    case 'boolean':
      return typeof value === 'boolean' ? undefined : 'not true/false';
    case 'color':
      return typeof value === 'string' && /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value) ? undefined : 'not a #RRGGBB colour';
    case 'image-choice':
      return typeof value === 'string' && field.choices.includes(value) ? undefined : 'not one of the images';
  }
}
